// Dependencias
import DataTable, { Api } from 'datatables.net-bs5';
import { format } from '@formkit/tempo';

// Utils
import { loadLoader } from '../utils/loader';
import { drawTable } from '../utils/drawTable';

// Configuraciones
import { generalConfig } from '../config/datatable.config';

// Services
import * as panelService from '../service/panel.service';

// Elementos HTML
const formSearchElement = document.querySelector<HTMLFormElement>('#form-search')!;
const tableJobsElement = document.querySelector<HTMLTableElement>('#whmcs-jobs-list')!;

/**
 * Calcula el estimado de reflejo en WHMCS considerando horas pico (8am–6pm CDMX).
 */
function estimatedWhmcsPeakHours(next_retry: string | Date | null | undefined, attempts: number): string {
  if (!next_retry) return '<span class="text-muted">Sin fecha estimada</span>';

  const date = new Date(next_retry as string);
  if (isNaN(date.getTime())) return '<span class="text-muted">Sin fecha estimada</span>';

  const getMxHour = (d: Date) => parseInt(
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Mexico_City',
      hour: 'numeric',
      hour12: false,
      hourCycle: 'h23'
    }).format(d)
  );

  const fmtMx = (d: Date) => new Intl.DateTimeFormat('es-MX', {
    timeZone: 'America/Mexico_City',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).format(d);

  const isPeak = (d: Date) => { const h = getMxHour(d); return h >= 8 && h < 18; };

  // Fuera de horas pico → ese intento será exitoso
  if (!isPeak(date)) {
    return `<span class="text-white d-block">${fmtMx(date)} CDMX</span>`
      + `<span class="badge bg-success mt-1"><i class="bi bi-lightning-charge-fill me-1"></i>Fuera de horas pico</span>`;
  }

  // En horas pico, primer intento (attempts=0) → fallará, estimar 2° intento (+5 min)
  if (attempts === 0) {
    const segundo = new Date(date.getTime() + 5 * 60 * 1000);
    const badge = !isPeak(segundo)
      ? `<span class="badge bg-success mt-1"><i class="bi bi-arrow-repeat me-1"></i>2° intento est. · fuera de pico</span>`
      : `<span class="badge bg-warning text-dark mt-1"><i class="bi bi-arrow-repeat me-1"></i>2° intento est. · horas pico</span>`;
    return `<span class="text-white d-block">${fmtMx(segundo)} CDMX</span>${badge}`;
  }

  // En horas pico, 2° intento o posterior (attempts>=1) → suele ser exitoso
  return `<span class="text-white d-block">${fmtMx(date)} CDMX</span>`
    + `<span class="badge bg-warning text-dark mt-1"><i class="bi bi-exclamation-triangle-fill me-1"></i>Horas pico — 2° intento</span>`;
}

// Variables globales
let jobsTable: Api<string[]>;
let currentPage = 1;
let lengthTable = 10;

export function main() {
  document.addEventListener('DOMContentLoaded', () => {
    // Se inicializa la tabla de Jobs
    jobsTable = new DataTable(tableJobsElement, Object.assign({}, generalConfig, {
      order: [[0, 'desc']] // Order by ID Job by default
    }));

    jobsTable.on('draw', async () => {
      const info = jobsTable.page.info();

      // Si la Pagina no es mayor a los datos establecidos, sale de la función
      if (!(info.page + 1 > currentPage)) return;

      // Optengo los datos de los filtros
      const json = Object.fromEntries(
        new FormData(formSearchElement)
      ) as ParamsSearch;
      currentPage = info.page + 1;

      loadLoader({});
      const token = document.querySelector<HTMLInputElement>('#access-token')!.value;
      const response = await panelService.fetchWhmcsJobs({
        token,
        page: currentPage,
        limit: lengthTable,
        status: json.selectSearch
      });
      
      if (response && response.jobs) {
        drawTable({
          data: response.jobs,
          table: jobsTable,
          addFinale: true,
          format: formatJobs
        });
      }
      loadLoader({ start: false });
    });

    // Cambio de tamaño de la tabla
    jobsTable.on('length', async (_, __, len) => {
      lengthTable = len;
      const json = Object.fromEntries(
        new FormData(formSearchElement)
      ) as ParamsSearch;

      loadLoader({});
      const token = document.querySelector<HTMLInputElement>('#access-token')!.value;
      const response = await panelService.fetchWhmcsJobs({
        token,
        page: 1,
        limit: lengthTable * 2,
        status: json.selectSearch
      });

      if (response && response.jobs) {
        drawTable({
          data: response.jobs,
          table: jobsTable,
          format: formatJobs
        });
      }
      currentPage = 1;
      loadLoader({ start: false });
    });
  });

  // Formulario para busqueda de información
  formSearchElement.addEventListener('submit', async (e) => {
    e.preventDefault();

    // Se optiene la información del formulario
    const target = e.target as HTMLFormElement;
    const json = Object.fromEntries(new FormData(target)) as ParamsSearch;

    // Se aplica al Loader de la aplicación
    loadLoader({});

    // Se realiza la optencion de valores para el servicio
    currentPage = 1;
    const token = document.querySelector<HTMLInputElement>('#access-token')!.value;
    
    // Si queremos filtrar en el futuro por estado, habría que actualizar la API para aceptarlo,
    // de momento usamos el status filter solo visual o si se integra.
    // fetchWhmcsJobs habría que modificarlo para recibir params adicionales.
    const response = await panelService.fetchWhmcsJobs({
      token,
      page: 1,
      limit: lengthTable * 2,
      status: json.selectSearch
    });

    if (response && response.jobs) {
      drawTable({
        table: jobsTable,
        data: response.jobs,
        format: formatJobs
      });
    }

    loadLoader({ start: false });
  });
}

/**
 *
 * @param data
 * @returns
 */
const formatJobs = (data: panelService.WhmcsSyncJob[]) =>
  data.map((job) => {
    let statusBadge = '';
    if (job.status === 'success') {
      statusBadge = '<span class="badge bg-success">Completado</span>';
    } else if (job.status === 'pending') {
      statusBadge = '<span class="badge bg-warning text-dark">Pendiente</span>';
    } else if (job.status === 'failed') {
      statusBadge = '<span class="badge bg-danger">Fallido</span>';
    } else {
      statusBadge = `<span class="badge bg-info text-dark">${job.status}</span>`;
    }

    return Object.values({
      id: `${job.id}`,
      entity_type: `<span class="badge bg-secondary">${job.entity_type}</span>`,
      entity_id: (() => {
        if (job.entity_type === 'client') {
          const whmcsBadge = job.whmcs_client_id
            ? `<span class="badge bg-primary d-block mb-1" title="ID en WHMCS"><i class="bi bi-cloud-check me-1"></i>WHMCS: ${job.whmcs_client_id}</span>`
            : '';
          const signlyBadge = job.entity_id
            ? `<span class="badge bg-secondary d-block" title="ID en Signly">Signly: ${job.entity_id}</span>`
            : '';
          const badges = [whmcsBadge, signlyBadge].filter(Boolean).join('');
          return badges || '<span class="text-muted">-</span>';
        }
        if (!job.entity_id) return '<span class="text-muted">-</span>';
        return job.is_whmcs_id
          ? `<span class="badge bg-primary" title="ID en WHMCS"><i class="bi bi-cloud-check me-1"></i>${job.entity_id}</span>`
          : `${job.entity_id}`;
      })(),
      email: job.email ? `${job.email}` : '<span class="text-muted">N/A</span>',
      status: statusBadge,
      attempts: `${job.attempts}`,
      estimated_whmcs: (['pending', 'failed'].includes(job.status))
        ? estimatedWhmcsPeakHours(job.next_retry, job.attempts)
        : '<span class="text-muted">-</span>',
      last_error: `<div style="max-width: 250px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" title="${job.last_error || ''}">${job.last_error || '-'}</div>`,
      created_at: job.created_at
        ? format({ date: job.created_at, format: 'short', locale: 'es' })
        : 'N/A'
    });
  });

// Definicion de tipos e interfaces
type ParamsSearch = {
  selectSearch: string;
};